import { api } from "@/lib/api";
import { CountyConfiguration } from "@/types";
import { ActionIcon, Button, Checkbox, Divider, Group, Modal, Pagination, Paper, Pill, PillsInput, Select, Stack, Table, Text, TextInput, Title, Tooltip } from "@mantine/core";
import { Form, useForm } from "@mantine/form";
import { IconArrowsSort, IconEdit, IconSortAscendingLetters, IconSortDescendingLetters, IconUsersGroup } from "@tabler/icons-react";
import { useEffect, useState } from "react";



const Counties = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(0);
    const [sortTerm, setSortTerm] = useState('name asc');
    const [counties, setCounties] = useState<CountyConfiguration[]>([]);
    const [visibleCounties, setVisibleCounties] = useState<CountyConfiguration[]>([]);
    const [filteredCounties, setFilteredCounties] = useState<CountyConfiguration[]>([]);
    const [selectedCounty, setSelectedCounty] = useState<CountyConfiguration | null>(null);
    const form = useForm({
        mode: 'uncontrolled',
        initialValues: {
            name: '',
            code: '',
            uiMask: '',
            dataMask: '',
            format: '',
            linkUrl: '',
            alertSendGridEmailTemplateId: '',
            createCountyMailer: false,
            mailerDocumentTypes: Array<string>(),
            mailerTemplatePath: '',
            useOnePageMailer: false,
            mailerOutputPath: '',
            useWatermark: false,
            isEnabled: false,
            addSampleToMailer: false,
            grayscaleMailer: false,
            deliveryEnabled: false,
        }
    });
    useEffect(() => {
        fetchCounties();
    }, []);
    const fetchCounties = async () => {
        try {
            const response: CountyConfiguration[] = await api.get(`/admin/counties`);
            setCounties(response);
        } catch (error) {
            console.error('Error fetching counties:', error);
        }
    }
    useEffect(() => {
        let newCounties = [...counties]
        if (searchTerm) {
            newCounties = [...newCounties.filter((county) => {
                return county?.name?.toLowerCase().includes(searchTerm?.toLowerCase()) || county?.code?.toLowerCase().includes(searchTerm?.toLowerCase());
            })];
        }
        setFilteredCounties(newCounties);
        setVisibleCounties([...newCounties.sort((a, b) => {
            if (sortTerm.includes('name')) {
                return sortTerm.includes('desc') ? b.name.localeCompare(a.name) : a.name.localeCompare(b.name);
            } else if (sortTerm.includes('code')) {
                return sortTerm.includes('desc') ? b.code.localeCompare(a.code) : a.code.localeCompare(b.code);
            }
            return 0;
        }).slice(page * pageSize, (page + 1) * pageSize)]);
    }, [page, pageSize, sortTerm, searchTerm, counties]);

    const handleEditCounty = (county: CountyConfiguration) => {
        form.setValues(county);
        setSelectedCounty(county);
    }
    const handleAddDocumentType = (value: string) => {
        const currentTypes = form.getValues().mailerDocumentTypes;
        value = value?.trim()?.toLocaleUpperCase();
        if (value && !currentTypes.includes(value)) {
            form.setFieldValue('mailerDocumentTypes', [...currentTypes, value]);
            selectedCounty!.mailerDocumentTypes.push(value);
        }
    }
    const handleSubmit = async () => {
        try {
            const values = form.getValues();
            await api.post('/admin/counties/update', { ...values, id: selectedCounty?.id });
            fetchCounties();
            setSelectedCounty(null);
        } catch (error) {
            console.error('Error editing county:', error);
        }
    }

    return (
        <>
            <Paper withBorder p='md' radius={'md'}>
                <Group justify='space-between'>
                    <Title order={4}>Counties</Title>
                    <IconUsersGroup size={22} stroke={1.5} />
                </Group>
                <Stack>
                    <Group justify='flex-end' mt='md'>
                        <TextInput
                            placeholder="Search by name or code"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.currentTarget.value)}
                        />
                        <Select
                            value={pageSize.toString()}
                            onChange={(value) => setPageSize(Number(value))}
                            data={[
                                { value: '10', label: '10' },
                                { value: '20', label: '20' },
                                { value: '50', label: '50' },
                                { value: '100', label: '100' }
                            ]}
                        />
                        <Pagination
                            total={Math.ceil(filteredCounties.length / pageSize)}
                            value={page + 1}
                            onChange={(value) => {
                                setPage(value - 1);
                            }}
                        />
                    </Group>
                    <Table>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th><Group tabIndex={0} role="button" onClick={() => {
                                    const newSortTerm = sortTerm.includes('Name') ? (sortTerm.includes('desc') ? 'name asc' : 'name desc') : 'name asc';
                                    setSortTerm(newSortTerm);
                                }}><Text>Name</Text>{sortTerm.includes('name') ? sortTerm.includes('desc') ? <IconSortDescendingLetters aria-hidden="true" /> : <IconSortAscendingLetters aria-hidden="true" /> : <IconArrowsSort aria-hidden="true" />}</Group></Table.Th>
                                <Table.Th><Group tabIndex={0} role="button" onClick={() => {
                                    const newSortTerm = sortTerm.includes('code') ? (sortTerm.includes('desc') ? 'code asc' : 'code desc') : 'code asc';
                                    setSortTerm(newSortTerm);
                                }}><Text>Code</Text>{sortTerm.includes('code') ? sortTerm.includes('desc') ? <IconSortDescendingLetters aria-hidden="true" /> : <IconSortAscendingLetters aria-hidden="true" /> : <IconArrowsSort aria-hidden="true" />}</Group></Table.Th>

                                <Table.Th><Group><Text>Enabled</Text></Group></Table.Th>
                                <Table.Th><Group><Text>Use Mailer</Text></Group></Table.Th>
                                <Table.Th><Group><Text>Actions</Text></Group></Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {visibleCounties.map((county) => (
                                <Table.Tr key={county.id}>
                                    <Table.Td>{county.name}</Table.Td>
                                    <Table.Td>{county.code}</Table.Td>
                                    <Table.Td>{county.isEnabled ? 'Yes' : 'No'}</Table.Td>
                                    <Table.Td>{county.createCountyMailer ? 'Yes' : 'No'}</Table.Td>
                                    <Table.Td>
                                        <Group>
                                            <Tooltip label={`Edit ${county.name}`} withArrow>
                                                <ActionIcon aria-label={`Edit ${county.name}`} variant='outline' color='pq-red' onClick={() => { handleEditCounty(county); }}>
                                                    <IconEdit size={16} />
                                                </ActionIcon>
                                            </Tooltip>
                                        </Group>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                    <Group justify='flex-end' mt='md'>
                        <Select
                            value={pageSize.toString()}
                            onChange={(value) => setPageSize(Number(value))}
                            data={[
                                { value: '10', label: '10' },
                                { value: '20', label: '20' },
                                { value: '50', label: '50' },
                                { value: '100', label: '100' }
                            ]}
                        />
                        <Pagination
                            total={Math.ceil(filteredCounties.length / pageSize)}
                            value={page + 1}
                            onChange={(value) => {
                                setPage(value - 1);
                            }}
                        />
                    </Group>
                </Stack>
            </Paper>
            <Modal
                size={'55%'}
                opened={!!selectedCounty}
                onClose={() => setSelectedCounty(null)}
                title={`Edit County: ${selectedCounty?.name}`}
            >
                <Form form={form} onSubmit={() => handleSubmit()}>
                    <Stack>
                        <Text size="sm">GuardDog Information:</Text>
                        <TextInput required label="Name" {...form.getInputProps('name')} />
                        <TextInput required label="Code" {...form.getInputProps('code')} />
                        <TextInput required label="UI Mask" {...form.getInputProps('uiMask')} />
                        <TextInput required label="Data Mask" {...form.getInputProps('dataMask')} />
                        <TextInput required label="Format" {...form.getInputProps('format')} />
                        <TextInput required label="Link URL" {...form.getInputProps('linkUrl')} />
                        <TextInput required label="Alert SendGrid Email Template ID" {...form.getInputProps('alertSendGridEmailTemplateId')} />
                        <Checkbox required label=" GuardDog Enabled" {...form.getInputProps('isEnabled', { type: 'checkbox' })} />
                        <Divider />
                        <Text size="sm">Mailer Information:</Text>
                        <PillsInput
                            label="Mailer Document Types"
                            {...form.getInputProps('mailerDocumentTypes')}

                        >
                            {selectedCounty?.mailerDocumentTypes?.map((type) => {
                                return (
                                    <Pill withRemoveButton={true} key={type} onRemove={() => {
                                        const currentTypes = form.getValues().mailerDocumentTypes;
                                        form.setFieldValue('mailerDocumentTypes', [...currentTypes.filter((t) => t !== type)]);
                                        selectedCounty!.mailerDocumentTypes = [...selectedCounty!.mailerDocumentTypes.filter((t) => t !== type)];
                                    }} >{type}</Pill>
                                );
                            })}
                            <PillsInput.Field placeholder=" Add document type" onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                    handleAddDocumentType(event.currentTarget.value);
                                    event.currentTarget.value = '';
                                }
                            }} />
                        </PillsInput>
                        <Checkbox label="Use One Page Mailer" {...form.getInputProps('useOnePageMailer', { type: 'checkbox' })} />
                        <Checkbox label="Add Sample to Mailer" {...form.getInputProps('addSampleToMailer', { type: 'checkbox' })} />
                        <Checkbox label="Use Watermark" {...form.getInputProps('useWatermark', { type: 'checkbox' })} />
                        <Checkbox label="Grayscale Mailer" {...form.getInputProps('grayscaleMailer', { type: 'checkbox' })} />
                        <TextInput label="Mailer Template Path" {...form.getInputProps('mailerTemplatePath')} />
                        <TextInput label="Mailer Output Path" {...form.getInputProps('mailerOutputPath')} />
                        <Checkbox label="Create County Mailer" {...form.getInputProps('createCountyMailer', { type: 'checkbox' })} />
                        <Checkbox label="Delivery Enabled" {...form.getInputProps('deliveryEnabled', { type: 'checkbox' })} />
                        <Button type="submit" color="pq-red">Update County</Button>
                    </Stack>
                </Form>
            </Modal>
        </>
    );
}

export default Counties;